/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorM2,
	ResumeIndicatorM2Data,
} from "@/services/esus/indicator/emulti/m2/getResumeIndicatorM2";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorM2ByHealthTeam,
	ResumeIndicatorM2ByHealthTeamData,
} from "@/services/esus/indicator/emulti/m2/getResumeIndicatorM2ByHealthTeam";
import {
	ResumeCitizenAttendancesIndicatorM2Data,
	getResumeCitizensAttendancesIndicatorM2,
} from "@/services/esus/indicator/emulti/m2/getResumeCitizensAttendancesIndicatorM2";

import { IndicatorEmultiM2Resume } from "./IndicatorEmultiM2Resume";
import { IndicatorEmultiM2HealthTeams } from "./IndicatorEmultiM2HealthTeams";
import { IndicatorEmultiM2Citizens } from "./IndicatorEmultiM2Citizens";

type IndicatorEmultiM2Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEmultiM2({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEmultiM2Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorM2, setResumeIndicatorM2] = useState<ResumeIndicatorM2Data>(
		new ResumeIndicatorM2Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorM2ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizens, setPageNumberCitizens] = useState(1);
	const [searchCitizens, setSearchCitizens] = useState("");
	const [isEvaluatedCitizens, setIsEvaluatedCitizens] = useState<boolean>();
	const debouncedSearchCitizen = useDebounce(searchCitizens, 2000);
	const [paginatedCitizens, setPaginatedCitizens] = useState(
		new Paginated<ResumeCitizenAttendancesIndicatorM2Data>()
	);
	const [isLoadingCitizens, setIsLoadingCitizens] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorM2(),
			handleGetResumeIndicatorM2ByHealthTeam(),
			handleGetResumeCitizensIndicatorM2(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorM2() {
		setResumeIndicatorM2(
			await getResumeIndicatorM2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorM2ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorM2ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorM2ByHealthTeam() {
		return (
			await getResumeIndicatorM2ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensIndicatorM2(pageNumber?: number) {
		setIsLoadingCitizens(true);
		setPaginatedCitizens(
			await getResumeCitizensAttendancesIndicatorM2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizens(false);
	}

	async function handleGetAllResumeCitizensAttendancesIndicatorM2() {
		return (
			await getResumeCitizensAttendancesIndicatorM2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: 1,
				pageSize: paginatedCitizens.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorM2ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorM2ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) handleGetResumeCitizensIndicatorM2(pageNumberCitizens);
	}, [pageNumberCitizens]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizens === 1) {
				handleGetResumeCitizensIndicatorM2(pageNumberCitizens);
			} else {
				setPageNumberCitizens(1);
			}
		}
	}, [debouncedSearchCitizen, isEvaluatedCitizens]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("M2 - Ações Interprofissionais realizadas por eMulti na APS");
	}, [setTitle]);

	return (
		<>
			<IndicatorEmultiM2Resume resume={resumeIndicatorM2} />
			<IndicatorEmultiM2HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorM2ByHealthTeam={
					handleGetAllResumeIndicatorM2ByHealthTeam
				}
			/>
			<IndicatorEmultiM2Citizens
				paginatedCitizens={paginatedCitizens}
				isLoading={isLoadingCitizens}
				search={searchCitizens}
				setSearch={setSearchCitizens}
				isEvaluated={isEvaluatedCitizens}
				setIsEvaluated={setIsEvaluatedCitizens}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberCitizens}
				setPageNumber={setPageNumberCitizens}
				handleGetAllResumeCitizensAttendancesIndicatorM2={
					handleGetAllResumeCitizensAttendancesIndicatorM2
				}
			/>
		</>
	);
}
